/*
 * <bh-date-picker> — accessible custom delivery calendar for the Bakehunters cake configurator.
 *
 * Visually disables (greys out + makes un-selectable):
 *   - past dates
 *   - specific unavailable dates (window.BakehuntersConfig.shop.blockedDates, ["YYYY-MM-DD", ...])
 *   - dates before the current size's lead time (the `min-lead-days` attribute, updated by selectSize)
 *
 * Closed days — weekly rest days (shop.blockedWeekdays, default [1,2] = Mon/Tue) and holidays
 * (shop.holidayDates) — are deliberately NOT disabled. They render muted but stay clickable, and
 * tapping one opens the #BhClosedDaySheet explainer (snippets/bh-closed-day-sheet.liquid): plenty
 * of customers want a cake for a day we are shut, and the real answer is "collect it the day
 * before, it keeps perfectly in the fridge", not a dead greyed-out cell. The sheet's CTA jumps
 * straight to that earlier day.
 *
 * That only holds while an open day is close enough to collect from. Beyond shop.closedMaxGapDays
 * (default 3) the soft block degrades to a hard one, so the middle of a long holiday shutdown
 * greys out rather than offering a collection date a week early. Suggestions are always EARLIER,
 * never later: a cake is bought for an occasion, and a date after it is no use.
 *
 * shop.workingDates (отработване) is the inverse: a day that stays OPEN despite the weekly
 * closure or a holiday, so a lost day can be made up. It outranks both — see _rawReason.
 *
 * On a valid selection it writes YYYY-MM-DD into the linked hidden input (default #DeliveryDate)
 * and dispatches a bubbling `change` on it, so the configurator's existing availability / rush /
 * time-slot logic runs unchanged. If the configurator rejects the date (e.g. same-day past cutoff)
 * and clears the input, the calendar deselects to stay in sync.
 *
 * Keyboard: arrows move day focus, PageUp/PageDown change month, Home/End jump to week start/end,
 * Enter/Space select. Roving tabindex + ARIA grid semantics.
 */
class BhDatePicker extends HTMLElement {
  static get observedAttributes() {
    return ['min-lead-days'];
  }

  constructor() {
    super();
    const cfg = (window.BakehuntersConfig && window.BakehuntersConfig.shop) || {};
    this.blockedWeekdays = this._parseArray(cfg.blockedWeekdays, [1, 2]);
    this.blockedDates = this._parseArray(cfg.blockedDates, []).map(String);
    // Holidays behave exactly like a rest day — muted but clickable, with the fridge explainer
    // — rather than like blockedDates, which are hard "we cannot make it" dates.
    this.holidayDates = this._parseArray(cfg.holidayDates, []).map(String);
    // Отработване — days we open despite the weekly closure, to make up for a holiday.
    // See the precedence note in _rawReason.
    this.workingDates = this._parseArray(cfg.workingDates, []).map(String);
    // How far back the explainer may point, in days. Covers the Sunday→Tuesday weekly closure
    // with a day to spare; past this the freshness pitch stops being honest.
    this.closedMaxGapDays = parseInt(cfg.closedMaxGapDays, 10) || 3;
    this.locale = (window.BakehuntersConfig && window.BakehuntersConfig.locale) === 'en' ? 'en' : 'bg';
    this.selected = null; // 'YYYY-MM-DD' or null
    this.minLeadDays = 0;
    this.focusKey = null; // roving-tabindex target 'YYYY-MM-DD'
    const now = new Date();
    this.viewYear = now.getFullYear();
    this.viewMonth = now.getMonth();
  }

  connectedCallback() {
    this.minLeadDays = parseInt(this.getAttribute('min-lead-days')) || 0;
    this.inputId = this.getAttribute('input-id') || 'DeliveryDate';
    this._advanceToMinMonth();
    this.render();
  }

  attributeChangedCallback(name, _oldV, newV) {
    if (name !== 'min-lead-days') return;
    this.minLeadDays = parseInt(newV) || 0;
    // A previously chosen date may now be too soon — clear it.
    if (this.selected && this._isDisabled(this.selected)) {
      this.selected = null;
      const input = document.getElementById(this.inputId || 'DeliveryDate');
      if (input) { input.value = ''; input.dispatchEvent(new Event('change', { bubbles: true })); }
    }
    this._advanceToMinMonth();
    if (this.isConnected) this.render();
  }

  /* ---------- date helpers ---------- */
  _parseArray(v, fallback) {
    if (Array.isArray(v)) return v;
    if (typeof v === 'string') {
      try { const p = JSON.parse(v); return Array.isArray(p) ? p : fallback; } catch (e) { return fallback; }
    }
    return fallback;
  }
  _pad(n) { return String(n).padStart(2, '0'); }
  _key(y, m, d) { return `${y}-${this._pad(m + 1)}-${this._pad(d)}`; }
  _minDate() {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    t.setDate(t.getDate() + this.minLeadDays);
    return t;
  }
  _dateOf(key) {
    const [y, m, d] = key.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  /**
   * Raw availability, WITHOUT the soft/hard decision:
   *   'lead'   — before today + the size's minimum lead time (includes past dates)
   *   'blocked'— an explicit unavailable date from admin
   *   'closed' — we are shut: a regular closed weekday, or a holiday
   *   null     — freely bookable
   *
   * Kept separate from _blockReason so the gap search below can ask "is this day open?"
   * without re-entering the soft/hard logic that depends on the search. Calling
   * _blockReason from the search would recurse forever.
   */
  _rawReason(key) {
    const date = this._dateOf(key);
    if (date < this._minDate()) return 'lead';
    if (this.blockedDates.includes(key)) return 'blocked';
    // Отработване. The position of this line IS the design:
    //   below 'lead'         — working a Monday cannot make a cake bakeable in less time;
    //   below blockedDates   — that list means "we cannot make it", which no amount of
    //                          working overrides;
    //   above holidayDates   — so a single day can be opened inside a longer closure
    //                          ("shut 28 Aug–3 Sep, but I'll work the 2nd");
    //   above blockedWeekdays— the core case: open a normally-closed Mon/Tue.
    if (this.workingDates.includes(key)) return null;
    if (this.holidayDates.includes(key)) return 'closed';
    if (this.blockedWeekdays.includes(date.getDay())) return 'closed';
    return null;
  }

  /** Is this closed day a holiday rather than a normal rest day? Drives the sheet's copy. */
  _isHoliday(key) {
    return this.holidayDates.includes(key) && !this.blockedWeekdays.includes(this._dateOf(key).getDay());
  }

  /**
   * Why a date can't be picked, or null when it's free.
   *
   * A closed day is a SOFT block — clickable, opens the explainer — only while there is an
   * open day near enough to actually offer. With nothing within reach the cell degrades to a
   * hard-`disabled` one, which is the honest signal in the middle of a long shutdown:
   * "it's 24 Aug, collect it on the 18th" is a worse answer than a greyed-out day.
   * Everything except 'closed' renders hard-disabled.
   */
  _blockReason(key) {
    const raw = this._rawReason(key);
    if (raw !== 'closed') return raw;
    return this._nearestOpenDay(key) ? 'closed' : 'blocked';
  }
  _isDisabled(key) {
    const reason = this._blockReason(key);
    return reason !== null && reason !== 'closed';
  }
  /**
   * Land the view on a month the customer can actually order from. Two steps:
   *   1. clamp forward to the month holding today + the size's lead time;
   *   2. keep stepping while that month has nothing freely bookable in it.
   *
   * Step 2 is the fix for a dead calendar. On Mon 31 Aug 2026 the clamp was a no-op (no size
   * picked yet, so lead time 0), days 1-30 were past, and the 31st was a closed Monday whose
   * backwards-only gap search found nothing but past days and so hard-blocked as well. The
   * result was 31 disabled cells with the prev arrow disabled too, the next arrow the only
   * way out of the month. Any last-day-of-month that is closed reproduces it.
   *
   * "Bookable" is `_blockReason`, the same predicate render() uses to pick the roving-tabindex
   * target, so a month whose only live cells are soft-closed Mon/Tue explainers does NOT
   * qualify. Those cells still render once we land somewhere; they just cannot be the reason
   * we landed, for the same reason they are never the default focus.
   *
   * Bounded at 12 months. A shop blocked out that far keeps the clamped month and shows an
   * empty calendar, which is at least true; hunting forever is not an improvement.
   */
  _advanceToMinMonth() {
    const [fy, fm] = this._floorMonth();
    if (new Date(this.viewYear, this.viewMonth, 1) < new Date(fy, fm, 1)) {
      this.viewYear = fy;
      this.viewMonth = fm;
    }
  }

  /**
   * The earliest month worth showing: the first one from the lead-time month onward that has a
   * bookable day in it. This is the floor for BOTH landing and backwards navigation.
   *
   * Nothing may page below it. A back-click can only ever answer "is there something earlier?",
   * and by construction the answer is always no, so the only reachable outcome is the dead grey
   * month this whole change exists to stop showing.
   *
   * Computed from the lead-time month rather than from the current view, so it stays put when
   * the customer has paged forward: browsing to December must not drag the floor with it, or
   * they could not get back.
   */
  _floorMonth() {
    const min = this._minDate();
    let y = min.getFullYear(), m = min.getMonth();
    for (let i = 0; i < 12; i++) {
      if (this._hasBookableDay(y, m)) return [y, m];
      if (++m > 11) { m = 0; y++; }
    }
    return [min.getFullYear(), min.getMonth()];
  }

  /** At least one freely bookable day this month? Soft-closed days deliberately do not count. */
  _hasBookableDay(y, m) {
    for (let d = 1, last = new Date(y, m + 1, 0).getDate(); d <= last; d++) {
      if (!this._blockReason(this._key(y, m, d))) return true;
    }
    return false;
  }
  _sameMonth(key, y, m) { return key && key.slice(0, 7) === `${y}-${this._pad(m + 1)}`; }
  _monthLabel() {
    return new Intl.DateTimeFormat(this.locale, { month: 'long', year: 'numeric' }).format(new Date(this.viewYear, this.viewMonth, 1));
  }
  _weekdayLabels() {
    const base = new Date(2024, 0, 1); // a Monday
    const out = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(base);
      d.setDate(base.getDate() + i);
      out.push(new Intl.DateTimeFormat(this.locale, { weekday: 'short' }).format(d));
    }
    return out;
  }
  _dayLabel(y, m, d, reason) {
    let label = new Intl.DateTimeFormat(this.locale, { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date(y, m, d));
    if (reason === 'closed') label += ' — ' + this._t('closedDay');
    else if (reason) label += ' — ' + this._t('unavailable');
    return label;
  }
  _t(k) {
    const s = {
      bg: {
        calendar: 'Календар за доставка', prev: 'Предишен месец', next: 'Следващ месец',
        unavailable: 'неналично', closedDay: 'затворено, натисни за други опции'
      },
      en: {
        calendar: 'Delivery calendar', prev: 'Previous month', next: 'Next month',
        unavailable: 'unavailable', closedDay: 'closed, tap for options'
      }
    };
    return (s[this.locale] || s.bg)[k] || k;
  }

  /* ---------- render ---------- */
  render() {
    const y = this.viewYear, m = this.viewMonth;
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const offset = (new Date(y, m, 1).getDay() + 6) % 7; // Monday-first leading blanks
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const todayKey = this._key(today.getFullYear(), today.getMonth(), today.getDate());

    // Roving-tabindex target: keep current focus if in view, else selected, else first selectable.
    let focusKey = this._sameMonth(this.focusKey, y, m) ? this.focusKey : null;
    if (!focusKey && this._sameMonth(this.selected, y, m)) focusKey = this.selected;
    if (!focusKey) {
      // First *freely bookable* day — closed weekdays are clickable but must never be the
      // default focus target, or the calendar opens pointing at a day you can't order for.
      for (let d = 1; d <= daysInMonth; d++) { const k = this._key(y, m, d); if (!this._blockReason(k)) { focusKey = k; break; } }
    }
    if (!focusKey) focusKey = this._key(y, m, 1);
    this.focusKey = focusKey;

    const [fy, fm] = this._floorMonth();
    const prevDisabled = new Date(y, m, 1) <= new Date(fy, fm, 1);
    const wd = this._weekdayLabels();

    let cells = '';
    for (let i = 0; i < offset; i++) cells += '<span class="bh-cal__pad" aria-hidden="true"></span>';
    for (let d = 1; d <= daysInMonth; d++) {
      const key = this._key(y, m, d);
      const reason = this._blockReason(key);
      const closed = reason === 'closed';
      const disabled = reason !== null && !closed;
      const selected = this.selected === key;
      const classes = ['bh-cal__day'];
      if (selected) classes.push('is-selected');
      if (closed) classes.push('is-closed');
      if (key === todayKey) classes.push('is-today');
      cells += `<button type="button" role="gridcell" class="${classes.join(' ')}" data-key="${key}" tabindex="${key === focusKey ? '0' : '-1'}"` +
        `${disabled ? ' disabled aria-disabled="true"' : ''}${selected ? ' aria-selected="true"' : ''}` +
        `${closed ? ' aria-haspopup="dialog"' : ''}` +
        ` aria-label="${this._dayLabel(y, m, d, reason)}">${d}</button>`;
    }

    this.innerHTML =
      `<div class="bh-cal" role="group" aria-label="${this._t('calendar')}">` +
        `<div class="bh-cal__header">` +
          `<button type="button" class="bh-cal__nav" data-nav="-1"${prevDisabled ? ' disabled' : ''} aria-label="${this._t('prev')}"><svg class="bh-cal__chev" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M15 5l-7 7 7 7"/></svg></button>` +
          `<div class="bh-cal__title" aria-live="polite">${this._monthLabel()}</div>` +
          `<button type="button" class="bh-cal__nav" data-nav="1" aria-label="${this._t('next')}"><svg class="bh-cal__chev" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9 5l7 7-7 7"/></svg></button>` +
        `</div>` +
        `<div class="bh-cal__grid" role="grid">` +
          `<div class="bh-cal__row bh-cal__row--head" role="row">` +
            wd.map(w => `<span class="bh-cal__wd" role="columnheader">${w}</span>`).join('') +
          `</div>` +
          `<div class="bh-cal__days" role="rowgroup">${cells}</div>` +
        `</div>` +
      `</div>`;
    this._bind();
  }

  _bind() {
    this.querySelectorAll('[data-nav]').forEach(b =>
      b.addEventListener('click', () => this._changeMonth(parseInt(b.dataset.nav))));
    this.querySelectorAll('.bh-cal__day').forEach(b => {
      b.addEventListener('click', () => { if (!b.disabled) this._select(b.dataset.key); });
      b.addEventListener('keydown', (e) => this._onKey(e, b.dataset.key));
    });
  }

  _changeMonth(delta) {
    let m = this.viewMonth + delta, y = this.viewYear;
    if (m < 0) { m = 11; y--; } else if (m > 11) { m = 0; y++; }
    const [fy, fm] = this._floorMonth();
    if (new Date(y, m, 1) < new Date(fy, fm, 1)) return;
    this.viewYear = y; this.viewMonth = m;
    this.focusKey = null;
    this.render();
    const nav = this.querySelector(`[data-nav="${delta}"]`);
    if (nav && !nav.disabled) nav.focus();
  }

  /**
   * @param {string} key  YYYY-MM-DD
   * @param {{focus?: boolean}} [opts]  focus: false for programmatic restores (cart edit), where
   *   focusing the day would scroll the page down to the calendar before the customer asked.
   */
  _select(key, { focus = true } = {}) {
    const reason = this._blockReason(key);
    if (reason === 'closed') { this.showClosedDayInfo(key); return; }
    if (reason) return;
    this.selected = key;
    const input = document.getElementById(this.inputId);
    if (input) {
      input.value = key;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      if (input.value === '') this.selected = null; // configurator rejected the date
    }
    this.render();
    if (this.selected && focus) {
      const el = this.querySelector(`[data-key="${this.selected}"]`);
      if (el) el.focus();
    }
  }

  /* ---------- closed-weekday explainer ---------- */

  /**
   * Nearest freely bookable day BEFORE `key`, within closedMaxGapDays. null when there is
   * none that close.
   *
   * Backwards only, deliberately. A cake is bought for a date — a birthday, an anniversary —
   * so a later day is not a smaller version of the same offer, it is useless: the occasion
   * has already passed. The only honest suggestion is "collect it earlier, it keeps perfectly
   * in the fridge", which is exactly what the explainer sells.
   *
   * Bounded because that pitch has a shelf life. It used to search a fortnight in both
   * directions, which was harmless for a two-day weekly closure and nonsense across a
   * week-long shutdown — it would cheerfully offer collection six days early. The bound is
   * what makes the middle of a long shutdown hard-block instead.
   *
   * Uses _rawReason only — see the note there.
   */
  _nearestOpenDay(key) {
    const probe = this._dateOf(key);
    for (let i = 0; i < this.closedMaxGapDays; i++) {
      probe.setDate(probe.getDate() - 1);
      const k = this._key(probe.getFullYear(), probe.getMonth(), probe.getDate());
      if (!this._rawReason(k)) return k;
    }
    return null;
  }

  /** Weekday name only, e.g. "неделя" — used to relabel the freshness timeline. */
  _weekdayName(key) {
    const d = this._dateOf(key);
    return new Intl.DateTimeFormat(this.locale, { weekday: 'long' }).format(d);
  }
  /**
   * The preposition that goes in front of a weekday.
   *
   * Bulgarian "в" becomes "във" before a word starting в- or ф- — "във вторник", but "в сряда",
   * "в неделя". It is a phonetic rule about the NEXT word, not a property of the date, so it is
   * derived from the weekday name rather than from a list of days: correct for вторник today and
   * for anything else the rule is ever applied to. Baking "на"/"в" into the locale string is what
   * produced "Затворени сме на сряда" and "тортата в вторник".
   *
   * English has no such alternation.
   */
  _prep(key) {
    if (this.locale !== 'bg') return 'on';
    return /^[вф]/i.test(this._weekdayName(key)) ? 'във' : 'в';
  }
  _dayAndMonth(key) {
    const d = this._dateOf(key);
    return new Intl.DateTimeFormat(this.locale, { weekday: 'long', day: 'numeric', month: 'long' }).format(d);
  }
  // Date for the freshness strip. `month: 'long'` on purpose — bg's short form is numeric
  // ("13.09"), which reads as a code rather than a date. Spelled out it is "13 септември".
  _dayAndMonthName(key) {
    return new Intl.DateTimeFormat(this.locale, { day: 'numeric', month: 'long' }).format(this._dateOf(key));
  }

  /**
   * Retune the sheet for whichever closed day was tapped.
   *
   * The static copy names Monday and Tuesday and walks a Sunday→Monday→Tuesday timeline. That
   * is correct for the weekly closure it was written for and wrong everywhere else, so:
   *
   *   - a HOLIDAY gets its own title and intro
   *   - the timeline is ALWAYS relabelled from the real dates, holiday or rest day, weekday
   *     plus day-of-month. "Monday" on its own does not say which Monday.
   *   - the static copy is the fallback for when there is no bookable day to suggest
   *
   * Every element restores from a stashed original, so reopening the sheet for a different
   * day never inherits the previous day's text.
   */
  /**
   * The days the freshness timeline walks through: [collect, in the fridge, still fresh].
   * The middle entry is null when there is no day to put there — the caller hides that step.
   *
   * IT NEVER RUNS PAST THE DAY THE CUSTOMER ASKED FOR. That day is always the last step.
   * Normally collection is two or more days earlier, so the middle is the day in between;
   * when collection is the day immediately before, the cake is only in the fridge overnight
   * and there is no whole day to name, so the middle is null. The fridge step still renders —
   * it is part of the story either way — it simply carries no date.
   *
   * An earlier version filled the empty middle by walking one day PAST the requested day,
   * to keep three steps. It put "1 октомври" on a sheet about being closed on 30 септември.
   * A later attempt kept that only while the extra day was itself closed — which is exactly
   * when it fires, because the closures are multi-day: 30 Sep–4 Oct is one shutdown, so
   * "1 октомври" came back. Both were wrong for the same reason. The timeline answers "what
   * happens to the cake I want on the 30th"; a date after the 30th is not part of that answer.
   *
   * The connector is drawn per-step precisely so a hidden step collapses cleanly; see the
   * `.bh-fresh__step + .bh-fresh__step::before` rule.
   */
  _freshnessDays(key, suggestion) {
    const dayAfter = (k) => {
      const d = this._dateOf(k);
      d.setDate(d.getDate() + 1);
      return this._key(d.getFullYear(), d.getMonth(), d.getDate());
    };
    const mid = dayAfter(suggestion);
    return mid === key ? [suggestion, null, key] : [suggestion, mid, key];
  }

  _applySheetCopy(sheet, key, suggestion) {
    // Always the DATED copy, weekly closure included: "Не работим в понеделник и вторник" is
    // false in an отработване week, when a Monday is a working day (2026-10-11). The sheet only
    // opens for a day that IS closed, so naming that day is true by construction. The static
    // Liquid copy is now just the pre-JS placeholder.

    const swap = (el, tpl) => {
      if (!el) return;
      if (el.dataset.bhCopy0 === undefined) el.dataset.bhCopy0 = el.textContent;
      // {in} pairs with {date}, {in_open} with {open} — each preposition agrees with the word
      // it precedes. '{in}' never matches inside '{in_open}': replace() takes a literal.
      // {weekday} is the requested day's weekday alone — "…свежа за сряда" wants the day, not
      // the full date, which the same sentence has already given for the collection day.
      // It takes "за", which never alternates, so it needs no {in} of its own.
      el.textContent = (tpl || el.dataset.bhCopy0)
        .replace('{in_open}', suggestion ? this._prep(suggestion) : '')
        .replace('{in}', this._prep(key))
        .replace('{weekday}', this._weekdayName(key))
        .replace('{date}', this._dayAndMonth(key))
        .replace('{open}', suggestion ? this._dayAndMonth(suggestion) : '');
    };

    const title = sheet.querySelector('[data-bh-title-holiday]');
    swap(title, title && title.dataset.bhTitleHoliday);

    // The dated intro needs a suggested day ("Вземи в неделя, 25 октомври…"). Without one it is
    // hidden rather than falling back to the static Mon/Tue sentence, which could be false.
    const intro = sheet.querySelector('[data-bh-intro-holiday]');
    if (intro) {
      swap(intro, intro.dataset.bhIntroHoliday);
      intro.hidden = !suggestion;
    }

    // Suggestions are always earlier than the requested day, so the freshness timeline always
    // applies — but with no suggestion at all there is nothing to walk through.
    const timeline = sheet.querySelector('[data-bh-fresh]');
    if (timeline) timeline.hidden = !suggestion;

    const days = sheet.querySelectorAll('[data-bh-fresh-day]');
    const dates = sheet.querySelectorAll('[data-bh-fresh-date]');
    days.forEach((el) => { if (el.dataset.bhDay0 === undefined) el.dataset.bhDay0 = el.textContent; });

    // The three steps are always [nearest open day, the day after, the requested day] — true of a
    // holiday closure and of the regular Mon/Tue one alike, so both now get real weekdays AND
    // real dates. This used to be gated on `holiday`, which left the weekly case showing the
    // static "Sunday / Monday / Tuesday" with no numbers at all; a customer looking at it could
    // not tell which Sunday they were being offered. The static copy survives only as the
    // no-suggestion fallback, where there is no date to show.
    // All three steps always render. A null label means that day does not exist as its own
    // date — collection is the day before, so the cake is only in the fridge overnight. The
    // fridge step still belongs in the story, it just carries no date: an empty date/weekday
    // collapses (`:empty`) and the shared subgrid keeps the other two columns in line.
    if (suggestion && days.length === 3) {
      const labels = this._freshnessDays(key, suggestion);
      days.forEach((el, i) => { el.textContent = labels[i] ? this._weekdayName(labels[i]) : ''; });
      dates.forEach((el, i) => { el.textContent = labels[i] ? this._dayAndMonthName(labels[i]) : ''; });
    } else {
      days.forEach((el) => { el.textContent = el.dataset.bhDay0; });
      dates.forEach((el) => { el.textContent = ''; });
    }
    days.forEach((el) => {
      const step = el.closest('.bh-fresh__step');
      if (step) step.hidden = false;
    });
  }

  showClosedDayInfo(key) {
    const sheet = document.getElementById('BhClosedDaySheet');
    if (!sheet) return; // sheet not rendered on this template — closed days simply do nothing
    // Clarity: taps vs accepts. "Lost" = sessions with bh_closed_day_tap but no bh_add_to_cart.
    window.bhClarityEvent?.('bh_closed_day_tap');
    const cta = sheet.querySelector('[data-bh-closed-cta]');
    const suggestion = this._nearestOpenDay(key);
    this._applySheetCopy(sheet, key, suggestion);

    if (cta && suggestion) {
      const [sy, sm, sd] = suggestion.split('-').map(Number);
      const when = new Intl.DateTimeFormat(this.locale, { weekday: 'long', day: 'numeric', month: 'long' })
        .format(new Date(sy, sm - 1, sd));
      cta.textContent = (cta.dataset.bhClosedCta || '{date}')
        .replace('{in}', this._prep(suggestion))
        .replace('{date}', when);
      cta.hidden = false;
      cta.onclick = () => {
        // Close first, then select — and do NOT hang this off the dialog's `close` event,
        // which does not reliably fire for these sheets (see bh-drawer.js). __bhClose also
        // runs the drawer's teardown; the native sheet.close() would leave <body> locked.
        if (sheet.__bhClose) sheet.__bhClose(); else sheet.close();
        window.bhClarityEvent?.('bh_closed_day_accept');
        this.viewYear = sy;
        this.viewMonth = sm - 1;
        this.focusKey = suggestion;
        this._select(suggestion);
        // bh-drawer returns focus to the opener when the sheet closes, and that opener cell
        // has just been destroyed by the re-render. Re-assert on the next task so focus ends
        // up on the day we picked instead of falling back to <body>.
        setTimeout(() => this.querySelector(`[data-key="${suggestion}"]`)?.focus(), 0);
      };
    } else if (cta) {
      cta.hidden = true; // nothing bookable within a fortnight — explainer only
    }

    const opener = this.querySelector(`[data-key="${key}"]`);
    if (sheet.__bhOpen) sheet.__bhOpen(opener);
    else if (!sheet.open) sheet.showModal(); // bh-drawer absent: still show something usable
  }

  _onKey(e, key) {
    const [y, m, d] = key.split('-').map(Number);
    const target = new Date(y, m - 1, d);
    let handled = true;
    switch (e.key) {
      case 'ArrowLeft': target.setDate(target.getDate() - 1); break;
      case 'ArrowRight': target.setDate(target.getDate() + 1); break;
      case 'ArrowUp': target.setDate(target.getDate() - 7); break;
      case 'ArrowDown': target.setDate(target.getDate() + 7); break;
      case 'Home': target.setDate(target.getDate() - ((target.getDay() + 6) % 7)); break;
      case 'End': target.setDate(target.getDate() + (6 - ((target.getDay() + 6) % 7))); break;
      case 'PageUp': target.setMonth(target.getMonth() - 1); break;
      case 'PageDown': target.setMonth(target.getMonth() + 1); break;
      case 'Enter': case ' ': e.preventDefault(); this._select(key); return;
      default: handled = false;
    }
    if (!handled) return;
    e.preventDefault();
    const [fy, fm] = this._floorMonth();
    if (new Date(target.getFullYear(), target.getMonth(), 1) < new Date(fy, fm, 1)) return;
    this.viewYear = target.getFullYear();
    this.viewMonth = target.getMonth();
    this.focusKey = this._key(target.getFullYear(), target.getMonth(), target.getDate());
    this.render();
    const el = this.querySelector(`[data-key="${this.focusKey}"]`);
    if (el) el.focus();
  }
}

if (!customElements.get('bh-date-picker')) {
  customElements.define('bh-date-picker', BhDatePicker);
}
